import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  BrowserRouter as Router,
  Navigate,
  Routes,
  Route,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import CaseStudyInstallments from "./pages/CaseStudyInstallments";
import CaseStudyCheckout from "./pages/CaseStudyCheckout";
import CaseStudyDesignSystem from "./pages/CaseStudyDesignSystem";
import CaseStudyAiWorkflow from "./pages/CaseStudyAiWorkflow";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import Work from "./pages/Work";
import AddToBagAnim from "./pages/AddToBagAnim";
import DocumentHead from "./components/portfolio/DocumentHead";
import PageAnalytics from "./components/portfolio/PageAnalytics";
import { recordOrigin } from "./data/backLinks";

// Page-to-page transition on the native View Transition API. The browser
// snapshots the screen before and after the route swap and fades the new
// picture in over the old one (the `.pf-page-transition` rules in index.css),
// so there is only ever one page in the DOM and nothing to pin or overlap
// afterwards. Browsers without the API, and readers who
// prefer reduced motion, get an instant swap.
//
// The routes render `shown` rather than the router's location, so the old page
// stays on screen until the browser has taken its picture of it.
//
// Scroll is restored here rather than by the browser. The browser restores on
// popstate, before the new route has rendered, which moved the page being left
// to the offset of the page being returned to. Each history entry's offset is
// kept by its location key and put back on Back / Forward; any other
// navigation to a new page opens at its top.
const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// A page put back at a saved offset is a fresh mount, and media above that
// offset can still be loading and pushing the content down, which would land
// the reader further into the page than they left it. The offset is re-applied
// while the page grows, for a second at most, and the reader's own scroll ends
// it. Returns the function that stops it early.
const READER_INPUT = ["wheel", "touchstart", "keydown", "pointerdown"];

function holdScroll(y) {
  window.scrollTo(0, y);
  const observer = new ResizeObserver(() => window.scrollTo(0, y));
  const stop = () => {
    observer.disconnect();
    clearTimeout(timer);
    READER_INPUT.forEach((type) => window.removeEventListener(type, stop));
  };
  const timer = setTimeout(stop, 1000);
  observer.observe(document.body);
  READER_INPUT.forEach((type) => window.addEventListener(type, stop, { passive: true }));
  return stop;
}

// A homepage section opened from a SectionLink. Smooth only within the same
// page; arriving from another page lands on it directly. Focus moves to the
// section so keyboard and screen-reader users continue from there.
function revealSection(id, smooth) {
  const target = document.getElementById(id);
  if (!target) return;
  target.scrollIntoView({
    behavior: smooth && !prefersReducedMotion() ? "smooth" : "auto",
    block: "start",
  });
  target.focus({ preventScroll: true });
}

const AnimatedRoutes = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [shown, setShown] = useState(location);
  const offsets = useRef(new Map());
  const lastNavigation = useRef(navigationType);
  const isFirstRender = useRef(true);
  const previousShown = useRef(location);
  const stopHolding = useRef(() => {});

  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Keyed by the history entry the browser is on, not the page React is
  // showing: the router renders a navigation asynchronously, and a scroll that
  // starts with it (an article's contents link) belongs to the new entry.
  useEffect(() => {
    const remember = () =>
      offsets.current.set(window.history.state?.key ?? "default", window.scrollY);
    window.addEventListener("scroll", remember, { passive: true });
    return () => window.removeEventListener("scroll", remember);
  }, []);

  useLayoutEffect(() => {
    if (location.key === shown.key) return;
    // An entry left before any scroll on it (a homepage link used straight
    // from the top) has no offset yet; the page is still where it was left.
    if (!offsets.current.has(shown.key)) offsets.current.set(shown.key, window.scrollY);
    if (navigationType !== "POP") {
      recordOrigin(location.key, shown.pathname, shown.key, location.pathname, navigationType === "REPLACE");
    }
    lastNavigation.current = navigationType;
    const samePage = location.pathname === shown.pathname;
    if (samePage || typeof document.startViewTransition !== "function" || prefersReducedMotion()) {
      setShown(location);
      return;
    }

    const root = document.documentElement;
    root.classList.add("pf-page-transition");
    const transition = document.startViewTransition(() => {
      flushSync(() => setShown(location));
    });
    // A transition interrupted by the next one rejects `ready`; its swap has
    // still run, so there is nothing to recover.
    transition.ready.catch(() => {});
    transition.finished.finally(() => root.classList.remove("pf-page-transition"));
  }, [location, navigationType, shown]);

  // Runs in the commit that puts the new page in the DOM, so inside a view
  // transition the "after" picture is already taken at the right offset.
  useLayoutEffect(() => {
    const previous = previousShown.current;
    previousShown.current = shown;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // A shared "/#about" link opened fresh.
      if (shown.hash) {
        requestAnimationFrame(() => revealSection(shown.hash.slice(1), false));
      }
      return;
    }
    stopHolding.current();
    const saved = offsets.current.get(shown.key);
    if (lastNavigation.current === "POP" && saved !== undefined) {
      stopHolding.current = holdScroll(saved);
    } else if (lastNavigation.current === "POP" && shown.hash) {
      document.getElementById(shown.hash.slice(1))?.scrollIntoView();
    } else if (lastNavigation.current !== "POP" && shown.hash && shown.state?.reveal) {
      revealSection(shown.hash.slice(1), previous.pathname === shown.pathname);
    } else if (lastNavigation.current !== "POP" && !shown.hash) {
      window.scrollTo(0, 0);
    }
  }, [shown]);

  return (
    <Routes location={shown}>
      <Route path="/" element={<Home />} />
      {/* Reached from the landing header. Runs on the article shell, so
          it reads as another page of the same document. */}
      <Route path="/work" element={<Work />} />
      {/* About lives on the homepage; old links land on that section. */}
      <Route
        path="/about"
        element={<Navigate to={{ pathname: "/", hash: "#about" }} state={{ reveal: true }} replace />}
      />
      <Route path="/case-study/installments" element={<CaseStudyInstallments />} />
      <Route path="/case-study/checkout" element={<CaseStudyCheckout />} />
      <Route path="/case-study/design-system" element={<CaseStudyDesignSystem />} />
      <Route path="/case-study/ai-workflow" element={<CaseStudyAiWorkflow />} />
      <Route path="/add-to-bag-demo" element={<AddToBagAnim />} />
      {/* Everything else, including the retired /project/engram and
          /project/trace articles. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <DocumentHead />
      <PageAnalytics />
      <AnimatedRoutes />
    </Router>
  );
}

export default App;
